import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import { Document, Page, Text, View, pdf } from '@react-pdf/renderer';
import { Orientation } from '@react-pdf/types';
import cl from 'classnames';
import {
  Account,
  Company,
  CompaniesSummaryRecord,
  PackageBillingRecord,
  HeadsetBillingRecord,
} from 'src/models';
import { RoleEnum } from 'src/security/roles';
import { HEADSETS, HOTELS } from 'src/constants';
import { usePage, useContextData, useLanguage } from 'src/hooks';
import {
  compareString,
  currencySign,
  formatDate,
  formatDuration,
  hybridCompare,
} from 'src/helper';
import { useAppDispatch } from 'src/store';
import { clearCompanySummaryState } from 'src/store/reducers/companiesSummary';
import { clearCompanyBillingState } from 'src/store/reducers/companyBilling';
import { clearReportsHeadsetsState } from 'src/store/reducers/reportHeadsets';
import { useAccount } from 'src/services/auth';
import { useCompanyById } from 'src/services/company';
import {
  useCompanyBillingState,
  useHeadsetsSucceededBillingByCompanyId,
  usePackagesBillingByCompanyId,
  fetchHeadsetsBillingAction,
  fetchPackagesBillingAction,
} from 'src/services/companyBilling';
import {
  useCompaniesSummary,
  useCompaniesSummaryState,
  fetchCompaniesSummaryAction,
} from 'src/services/companiesSummary';
import {
  PageSwitcher,
  PrintTable,
  StickyTable,
  useStickyCols,
  STSortOption,
  StickyTableConfig,
  PdfTableView,
  PdfTablePreparer,
  PdfTableParams,
  PdfHeightChecker,
  pdfFontTranslate,
  PdfModal,
} from 'src/components';
import {
  Button,
  Select,
  FilterModal,
  FilterModalButton,
  useDateRangePicker,
  FilterModalIndicator,
  Link,
} from 'src/components/general';
import styles from './hotelBillingStyle.module.scss';

export const HotelBilling = () => {
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const { hotel: companyId } = useParams() as { hotel: string };
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();
  const account = useAccount() as Account;
  const company = useCompanyById(companyId) as Company;
  const state = useCompanyBillingState();
  const summaryState = useCompaniesSummaryState();
  const headsetsRecords = useHeadsetsSucceededBillingByCompanyId(companyId);
  const packagesRecords = usePackagesBillingByCompanyId(companyId);
  const companiesSummaryRecords = useCompaniesSummary();

  const [isHeadsetsLoading, setIsHeadsetsLoading] = useState(
    state.isHeadsetsPending,
  );
  const [isPackagesLoading, setIsPackagesLoading] = useState(
    state.isPackagesPending,
  );
  const [isSummaryLoading, setIsSummaryLoading] = useState(
    summaryState.isPending,
  );
  const [isFiltered, setIsFiltered] = useState(false);

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [tableNumber, setTableNumber] = useState<1 | 2 | 3>(1);
  const [headsetsSortOption, setHeadsetsSortOption] =
    useState<STSortOption | null>(null);
  const [packagesSortOption, setPackagesSortOption] =
    useState<STSortOption | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: isHeadsetsLoading || isPackagesLoading || isSummaryLoading,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );
  const tableNumbers = useMemo(
    () => [
      {
        value: 1,
        label: t('page.hotel.billing.tableNumber', { number: 1 }),
      },
      {
        value: 2,
        label: t('page.hotel.billing.tableNumber', { number: 2 }),
      },
      {
        value: 3,
        label: t('page.hotel.billing.tableNumber', { number: 3 }),
      },
    ],
    [t],
  );

  const pageBreakItems = useMemo(
    () => [
      { id: 't2', label: t('page.hotel.billing.tableNumber', { number: 2 }) },
      { id: 't3', label: t('page.hotel.billing.tableNumber', { number: 3 }) },
    ],
    [t],
  );
  const initPageBreakParams = useMemo(() => ({ t2: null, t3: null }), []);
  const [pageBreakParams, setPageBreakParams] =
    useState<Record<string, boolean | null>>(initPageBreakParams);

  const [showExportPdf, setShowExportPdf] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfHeaderHeightProps, setPdfHeaderHeightProps] = useState<{
    isActive: boolean;
    orientation?: Orientation;
    clbHeight: number;
    height: number | null;
  }>({
    isActive: false,
    clbHeight: 0,
    height: null,
  });
  const [pdfHeadsetsTableProps, setPdfHeadsetsTableProps] = useState<{
    isActive: boolean;
    orientation?: Orientation;
    params: PdfTableParams | null;
  }>({ isActive: false, params: null });
  const [pdfPackagesTableProps, setPdfPackagesTableProps] = useState<{
    isActive: boolean;
    orientation?: Orientation;
    params: PdfTableParams | null;
  }>({ isActive: false, params: null });
  const [pdfCompaniesTableProps, setPdfCompaniesTableProps] = useState<{
    isActive: boolean;
    orientation?: Orientation;
    params: PdfTableParams | null;
  }>({ isActive: false, params: null });
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const rangePrintText = useMemo(() => {
    if (!range.start || !range.end) {
      return null;
    }

    return (
      <div className={styles.printText}>
        <span>{t('general.interval')}</span>
        {range.start.localString} - {range.end.localString}
      </div>
    );
  }, [range, t]);

  const rangePfdText = useMemo(() => {
    if (!range.start || !range.end) {
      return null;
    }

    return (
      <View
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <View style={{ marginRight: '3pt' }}>
          <Text
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {t('general.interval')}
          </Text>
        </View>
        <View style={{ marginRight: '3pt' }}>
          <Text
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            {range.start.localString} - {range.end.localString}
          </Text>
        </View>
      </View>
    );
  }, [range, t]);

  const onPdfHandle = useCallback(
    (orientation: Orientation) => {
      setIsPdfLoading(true);
      setPageBreakParams({ ...initPageBreakParams });
      setPdfHeaderHeightProps(() => ({
        isActive: true,
        clbHeight: rangePfdText ? 58 : 48,
        height: null,
        orientation,
      }));
      setPdfHeadsetsTableProps(() => ({
        isActive: true,
        params: null,
        orientation,
      }));
      setPdfPackagesTableProps(() => ({
        isActive: true,
        params: null,
        orientation,
      }));
      setPdfCompaniesTableProps(() => ({
        isActive: true,
        params: null,
        orientation,
      }));
    },
    [rangePfdText, initPageBreakParams],
  );

  useEffect(() => {
    if (
      pdfHeadsetsTableProps.isActive ||
      !pdfHeadsetsTableProps.params ||
      pdfPackagesTableProps.isActive ||
      !pdfPackagesTableProps.params ||
      pdfCompaniesTableProps.isActive ||
      !pdfCompaniesTableProps.params ||
      pdfHeaderHeightProps.isActive ||
      pdfHeaderHeightProps.height === null
    ) {
      return;
    }

    const pageHeight =
      pdfHeaderHeightProps.orientation === 'landscape' ? 718 : 1046;

    const offset = 20;

    const packBreak =
      pageHeight -
        ((pdfHeaderHeightProps.height +
          8 +
          pdfHeadsetsTableProps.params.tableHeight +
          8) %
          pageHeight) +
        offset <
      pdfPackagesTableProps.params.tableHeight;

    const compBreak = packBreak
      ? pageHeight -
          ((pdfPackagesTableProps.params.tableHeight + 8) % pageHeight) +
          offset <
        pdfCompaniesTableProps.params.tableHeight
      : pageHeight -
          ((pdfHeaderHeightProps.height +
            8 +
            pdfHeadsetsTableProps.params.tableHeight +
            8 +
            pdfPackagesTableProps.params.tableHeight +
            8) %
            pageHeight) +
          offset <
        pdfCompaniesTableProps.params.tableHeight;

    setPageBreakParams({ t2: packBreak, t3: compBreak });
  }, [
    pdfCompaniesTableProps.isActive,
    pdfCompaniesTableProps.params,
    pdfHeaderHeightProps.height,
    pdfHeaderHeightProps.isActive,
    pdfHeaderHeightProps.orientation,
    pdfHeadsetsTableProps.isActive,
    pdfHeadsetsTableProps.params,
    pdfPackagesTableProps.isActive,
    pdfPackagesTableProps.params,
  ]);

  useEffect(() => {
    if (
      pdfHeadsetsTableProps.isActive ||
      !pdfHeadsetsTableProps.params ||
      pdfPackagesTableProps.isActive ||
      !pdfPackagesTableProps.params ||
      pdfCompaniesTableProps.isActive ||
      !pdfCompaniesTableProps.params ||
      pdfHeaderHeightProps.isActive ||
      pdfHeaderHeightProps.height === null ||
      pageBreakParams.t2 === null ||
      pageBreakParams.t3 === null
    ) {
      return;
    }

    pdf(
      <Document>
        <Page
          size="A4"
          style={{
            flexDirection: 'column',
            backgroundColor: '#FFF',
            padding: '28pt 28pt',
          }}
          orientation={pdfHeaderHeightProps.orientation}
          dpi={96}
        >
          <View
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Text
              style={{
                display: 'flex',
                fontFamily: 'Inter',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {company?.name}
            </Text>
          </View>
          {rangePfdText}
          <View
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <View style={{ marginRight: '3pt' }}>
              <Text
                style={{
                  fontFamily: 'Inter',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {t('page.hotel.billing.todayPaymentCount')}
              </Text>
            </View>
            <View style={{ marginRight: '3pt' }}>
              <Text
                style={{
                  fontFamily: 'Inter',
                  fontSize: 12,
                  fontWeight: 400,
                }}
              >
                {company?.todayPaymentCount || 0}
              </Text>
            </View>
          </View>
          <PdfTableView
            params={pdfHeadsetsTableProps.params}
            style={{ marginTop: '6pt' }}
          />
          <PdfTableView
            params={pdfPackagesTableProps.params}
            style={{ marginTop: '6pt' }}
            breakPage={pageBreakParams.t2}
          />
          <PdfTableView
            params={pdfCompaniesTableProps.params}
            style={{ marginTop: '6pt' }}
            breakPage={pageBreakParams.t3}
          />
        </Page>
      </Document>,
    )
      .toBlob()
      .then((blob) => {
        setPdfBlob(blob);
        setIsPdfLoading(false);
      });
  }, [
    pdfHeaderHeightProps.isActive,
    pdfHeaderHeightProps.height,
    pdfHeaderHeightProps.orientation,
    pdfHeadsetsTableProps.isActive,
    pdfHeadsetsTableProps.params,
    pdfPackagesTableProps.isActive,
    pdfPackagesTableProps.params,
    pdfCompaniesTableProps.isActive,
    pdfCompaniesTableProps.params,
    pageBreakParams.t2,
    pageBreakParams.t3,
    rangePfdText,
    company?.name,
    company?.todayPaymentCount,
    t,
  ]);

  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    if (!company || !account?.role) {
      return;
    }

    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    setIsHeadsetsLoading(true);
    setIsFiltered(isRangeChanged);

    dispatch(
      fetchHeadsetsBillingAction({
        companyId,
        params: {
          start,
          end,
        },
      }),
    );

    if (account?.role !== RoleEnum.airpano) {
      setIsPackagesLoading(true);
      dispatch(
        fetchPackagesBillingAction({
          companyId,
          params: {
            start,
            end,
          },
        }),
      );

      setIsSummaryLoading(true);
      dispatch(
        fetchCompaniesSummaryAction({
          params: {
            start,
            end,
            companyId,
          },
        }),
      );
    }
  }, [dispatch, company, companyId, range, account?.role, isRangeChanged]);

  useEffect(() => {
    setIsHeadsetsLoading(state.isHeadsetsPending);
  }, [state.isHeadsetsPending]);

  useEffect(() => {
    setIsPackagesLoading(state.isPackagesPending);
  }, [state.isPackagesPending]);

  useEffect(() => {
    setIsSummaryLoading(summaryState.isPending);
  }, [summaryState.isPending]);

  useEffect(
    () => () => {
      dispatch(clearCompanySummaryState());
      dispatch(clearCompanyBillingState());
    },
    [dispatch],
  );

  const summaryHeadsetsRecord: HeadsetBillingRecord = useMemo(() => {
    let totalSucceeded = 0;
    let totalSucceededCount = 0;
    let totalPending = 0;
    let totalCanceled = 0;
    let totalPaymentSystem = 0;
    let totalQrCode = 0;
    let totalReception = 0;
    let totalVipAccess = 0;
    let totalRoomBill = 0;

    headsetsRecords.forEach((record) => {
      totalSucceededCount += record.totalSucceededCount;
      if (account?.role !== RoleEnum.airpano) {
        totalSucceeded += record.totalSucceeded;
        totalPending += record.totalPending;
        totalCanceled += record.totalPending;
        totalPaymentSystem += record.paymentSystemSucceeded;
        totalQrCode += record.qrCodeSucceeded;
        totalReception += record.deskSucceeded;
        totalRoomBill += record.roomBillSucceeded;
        totalVipAccess += record.vipAccessSucceeded;
      }
    });
    return {
      id: '-1',
      phyDeviceId: '-1',
      label: t('general.total'),
      roomLabel: '-',
      isActive: true,
      totalSucceededCount: totalSucceededCount,
      totalSucceeded: totalSucceeded,
      totalCanceled: totalCanceled,
      totalPending: totalPending,
      paymentSystemSucceeded: totalPaymentSystem,
      qrCodeSucceeded: totalQrCode,
      deskSucceeded: totalReception,
      roomBillSucceeded: totalRoomBill,
      vipAccessSucceeded: totalVipAccess,
    };
  }, [account?.role, headsetsRecords, t]);

  const companiesSummaryData: CompaniesSummaryRecord[] = useMemo(
    () =>
      companiesSummaryRecords.map((record) => {
        const detail = record.details[0];
        const comm = (detail?.agentCommission || 0) / 100;

        return {
          ...record,
          legalEntityName: detail?.legalEntityName || '---',
          endUsersTotal: record.total - record.vip,
          agentTotal: detail
            ? record.total * comm - record.vip * (1 - comm)
            : '---',
        };
      }),
    [companiesSummaryRecords],
  );

  const headsetBillingConfigBase: StickyTableConfig<HeadsetBillingRecord>[] =
    useMemo(
      () => [
        {
          title: t('page.hotel.billing.headset.label'),
          field: 'label',
          key: 'label',
          sorter: (a, b, sortOrder) =>
            hybridCompare(a.label, b.label) *
            (sortOrder === 'descend' ? -1 : 1),
          render: (value, headset) => {
            return (
              <Link
                className={cl(
                  styles.label,
                  !accessMap[HEADSETS].length && styles.inactive,
                )}
                src={
                  accessMap[HEADSETS].length
                    ? `/${HOTELS}/${companyId}/${HEADSETS}/${headset.id}/${accessMap[HEADSETS][0]}`
                    : null
                }
                onClick={() => {
                  if (!accessMap[HEADSETS].length) {
                    return;
                  }

                  dispatch(clearReportsHeadsetsState());
                  setBackUrl(null);
                }}
              >
                {value}
              </Link>
            );
          },
          printRender: (value) => value,
          pdfRender: (value) => (
            <Text
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 10,
              }}
            >
              {value}
            </Text>
          ),
          summaryRender: (value) => value,
        },
        {
          title: t('page.hotel.billing.headset.roomLabel'),
          field: 'roomLabel',
          key: 'roomLabel',
          sorter: (a, b, sortOrder) =>
            compareString(a.roomLabel, b.roomLabel) *
            (sortOrder === 'descend' ? -1 : 1),
        },
        {
          title: t('page.hotel.billing.headset.totalSucceededCount'),
          field: 'totalSucceededCount',
          key: 'totalSucceededCount',
          sorter: (a, b, sortOrder) =>
            (a.totalSucceededCount - b.totalSucceededCount) *
            (sortOrder === 'descend' ? -1 : 1),
        },
      ],
      [companyId, t, accessMap, setBackUrl, dispatch],
    );

  const headsetBillingConfig: StickyTableConfig<HeadsetBillingRecord>[] =
    useMemo(
      () => [
        ...headsetBillingConfigBase,
        ...(account?.role === RoleEnum.airpano
          ? []
          : ([
              {
                title: t('page.hotel.billing.headset.totalSucceeded'),
                field: 'totalSucceeded',
                key: 'totalSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.totalSucceeded - b.totalSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
              {
                title: t('page.hotel.billing.headset.paymentSystemSucceeded'),
                field: 'paymentSystemSucceeded',
                key: 'paymentSystemSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.paymentSystemSucceeded - b.paymentSystemSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
              {
                title: t('page.hotel.billing.headset.qrCodeSucceeded'),
                field: 'qrCodeSucceeded',
                key: 'qrCodeSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.qrCodeSucceeded - b.qrCodeSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
              {
                title: t('page.hotel.billing.headset.deskSucceeded'),
                field: 'deskSucceeded',
                key: 'deskSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.deskSucceeded - b.deskSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
              {
                title: t('page.hotel.billing.headset.roomBillSucceeded'),
                field: 'roomBillSucceeded',
                key: 'roomBillSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.roomBillSucceeded - b.roomBillSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
              {
                title: 'VIP',
                field: 'vipAccessSucceeded',
                key: 'vipAccessSucceeded',
                sorter: (a, b, sortOrder) =>
                  (a.vipAccessSucceeded - b.vipAccessSucceeded) *
                  (sortOrder === 'descend' ? -1 : 1),
                render: (value) =>
                  `${value} ${currencySign(company?.location?.currency || 'rub')}`,
              },
            ] as StickyTableConfig<HeadsetBillingRecord>[])),
      ],
      [t, company?.location?.currency, account?.role, headsetBillingConfigBase],
    );

  const packageBillingConfig: StickyTableConfig<PackageBillingRecord>[] =
    useMemo(
      () => [
        {
          title: t('page.hotel.billing.package.name'),
          field: 'name',
          key: 'name',
          sorter: (a, b, sortOrder) =>
            compareString(a.name, b.name) * (sortOrder === 'descend' ? -1 : 1),
          render: (value, record) => {
            return (
              <div className={styles.name}>
                <div>
                  <div>{value}</div>
                  <div className={styles.secondary}>
                    {formatDate(record.createdAt) || '-'}
                  </div>
                </div>
                {record.archived && (
                  <div className={cl(styles.tag, styles.warning)}>
                    {t('general.archived')}
                  </div>
                )}
              </div>
            );
          },
          printRender: (value, record) => (
            <div className={styles.namePrint}>
              <div>
                <div>{value}</div>
                <div className={styles.secondary}>
                  {formatDate(record.createdAt) || '-'}
                </div>
              </div>
              {record.archived && (
                <div className={cl(styles.tag, styles.tagPrint)}>
                  {t('general.archived')}
                </div>
              )}
            </div>
          ),
          pdfRender: (value, record) => (
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '95%',
              }}
            >
              <View style={{ display: 'flex', flexDirection: 'column' }}>
                <View>
                  <Text
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: 10,
                    }}
                  >
                    {value}
                  </Text>
                </View>
                <View>
                  <Text
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: 9,
                      lineHeight: 1.33,
                    }}
                  >
                    {formatDate(record.createdAt) || '-'}
                  </Text>
                </View>
              </View>
              {record.archived && (
                <View
                  style={{
                    padding: '3pt 12pt',
                    borderRadius: 15,
                    minWidth: 68,
                    marginLeft: 12,
                    border: '1pt solid #333',
                  }}
                >
                  <Text
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      textAlign: 'center',
                      ...pdfFontTranslate(9, 1.42),
                    }}
                  >
                    {t('general.archived')}
                  </Text>
                </View>
              )}
            </View>
          ),
        },
        {
          title: t('page.hotel.billing.package.duration'),
          field: 'duration',
          key: 'duration',
          sorter: (a, b, sortOrder) =>
            (a.duration - b.duration) * (sortOrder === 'descend' ? -1 : 1),
          render: (value) => formatDuration(value),
        },
        {
          title: t('page.hotel.billing.package.price'),
          field: 'price',
          key: 'price',
          sorter: (a, b, sortOrder) =>
            (a.price - b.price) * (sortOrder === 'descend' ? -1 : 1),
          render: (value) =>
            `${value} ${currencySign(company?.location?.currency || 'rub')}`,
        },
        {
          title: t('page.hotel.billing.package.count'),
          field: 'count',
          key: 'count',
          sorter: (a, b, sortOrder) =>
            (a.count - b.count) * (sortOrder === 'descend' ? -1 : 1),
        },
        {
          title: t('page.hotel.billing.package.total'),
          field: 'total',
          key: 'total',
          sorter: (a, b, sortOrder) =>
            (a.total - b.total) * (sortOrder === 'descend' ? -1 : 1),
          render: (value) =>
            `${value} ${currencySign(company?.location?.currency || 'rub')}`,
        },
      ],
      [company?.location?.currency, t],
    );

  const companiesSummaryConfig: StickyTableConfig<CompaniesSummaryRecord>[] =
    useMemo(
      () => [
        {
          title: t('page.hotel.billing.companiesSummary.headsets'),
          field: 'headsets',
          key: 'headsets',
        },
        {
          title: t('page.hotel.billing.companiesSummary.endUsersTotal'),
          field: 'endUsersTotal',
          key: 'endUsersTotal',
          render: (value) =>
            `${value} ${currencySign(company?.location?.currency || 'rub')}`,
        },
        {
          title: t('page.hotel.billing.companiesSummary.vip'),
          field: 'vip',
          key: 'vip',
          render: (value) =>
            `${value} ${currencySign(company?.location?.currency || 'rub')}`,
        },
        {
          title: t('page.hotel.billing.companiesSummary.total'),
          field: 'total',
          key: 'total',
          render: (value) =>
            `${value} ${currencySign(company?.location?.currency || 'rub')}`,
        },
        {
          title: t('page.hotel.billing.companiesSummary.agentTotal'),
          field: 'agentTotal',
          key: 'agentTotal',
          render: (value) =>
            typeof value === 'string'
              ? value
              : `${parseFloat(value).toFixed(2)} ${currencySign(company?.location?.currency || 'rub')}`,
        },
      ],
      [company?.location?.currency, t],
    );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config: headsetBillingConfig,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  const printTop = useMemo(
    () => (
      <>
        <div className={styles.printLabel}>{company?.name}</div>
        {rangePrintText}
        <div className={styles.printText}>
          <span>{t('page.hotel.billing.todayPaymentCount')}</span>
          {company?.todayPaymentCount || 0}
        </div>
      </>
    ),
    [company?.name, company?.todayPaymentCount, rangePrintText, t],
  );

  return (
    <div className={styles.container}>
      <div className={styles.printHidden}>
        <div className={styles.printWrapper} ref={printRef}>
          <div className={styles.printArea}>
            {printTop}
            <PrintTable
              config={headsetBillingConfig}
              data={headsetsRecords}
              summaryRecord={summaryHeadsetsRecord}
              sortOption={headsetsSortOption}
              className={styles.printTable}
            />
            <PrintTable
              config={packageBillingConfig}
              data={packagesRecords}
              sortOption={packagesSortOption}
              className={cl(styles.printTable, styles.pageBreakInside)}
            />
            <PrintTable
              config={companiesSummaryConfig}
              data={companiesSummaryData}
              className={cl(styles.printTable, styles.pageBreakInside)}
            />
          </div>
        </div>
      </div>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          applyDRPChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {tableNumber === 1 && modalStickyColsFilter}
      </FilterModal>
      <PdfModal
        show={showExportPdf}
        close={() => {
          setShowExportPdf(false);
          setIsPdfLoading(false);
          setPageBreakParams({ ...initPageBreakParams });
          setPdfHeaderHeightProps({
            isActive: false,
            clbHeight: 0,
            height: null,
          });
          setPdfHeadsetsTableProps({ isActive: false, params: null });
          setPdfPackagesTableProps({ isActive: false, params: null });
          setPdfCompaniesTableProps({ isActive: false, params: null });
        }}
        pdfBlob={pdfBlob}
        fileName={`billing_${(company?.name ?? 'company').replaceAll(' ', '_')}.pdf`}
        isLoading={isPdfLoading}
        onPdfHandle={onPdfHandle}
        pageBreakItems={pageBreakItems}
        pageBreakParams={pageBreakParams}
        setPageBreakParams={setPageBreakParams}
      />
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          {account?.role !== RoleEnum.airpano && (
            <Select
              value={tableNumber}
              items={tableNumbers}
              selectItem={(section) => {
                setTableNumber(section as 1 | 2 | 3);
              }}
              design="switcher"
              className={styles.select}
            />
          )}
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(rangeIndicatorText)}
              disabled={
                isHeadsetsLoading || isPackagesLoading || isSummaryLoading
              }
            />
            {account?.role !== RoleEnum.airpano && (
              <>
                <FilterModalButton
                  setShow={() => {
                    setShowExportPdf(true);
                    onPdfHandle('portrait');
                  }}
                  icon="exportToPdf"
                  disabled={
                    isHeadsetsLoading || isPackagesLoading || isSummaryLoading
                  }
                />
                <ReactToPrint
                  content={() => printRef.current}
                  trigger={() => (
                    <button
                      className={styles.filterButton}
                      disabled={Boolean(
                        isHeadsetsLoading ||
                          isPackagesLoading ||
                          isSummaryLoading,
                      )}
                    >
                      <svg
                        width="23"
                        height="23"
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.19982 17.8002H3.09991C2.54298 17.8002 2.00886 17.5789 1.61505 17.1851C1.22124 16.7913 1 16.2572 1 15.7003V10.4505C1 9.89356 1.22124 9.35944 1.61505 8.96564C2.00886 8.57183 2.54298 8.35059 3.09991 8.35059H19.8992C20.4561 8.35059 20.9902 8.57183 21.384 8.96564C21.7778 9.35944 21.9991 9.89356 21.9991 10.4505V15.7003C21.9991 16.2572 21.7778 16.7913 21.384 17.1851C20.9902 17.5789 20.4561 17.8002 19.8992 17.8002H17.7993"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.8006 13.6006H5.20117V22.0002H17.8006V13.6006Z"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.20117 8.34968V1H17.8006V8.34968"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                />
              </>
            )}
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={
                isHeadsetsLoading || isPackagesLoading || isSummaryLoading
              }
            />
          </div>
        </div>
        <div className={styles.filters}>
          {dateRangePicker}
          {stickyColsFilter}
        </div>
      </div>
      {account?.role !== RoleEnum.airpano && (
        <div
          className={cl(
            styles.todayPaymentCountContainer,
            tableNumber !== 1 && styles.hideOnLaptop,
          )}
          style={{
            minWidth: headerWidth,
            maxWidth: headerWidth,
          }}
        >
          <div className={styles.todayPaymentCount}>
            <div className={styles.countText}>
              <span>{t('page.hotel.billing.todayPaymentCount')}</span>
              {company?.todayPaymentCount || 0}
            </div>
            <div className={styles.buttons}>
              <Button
                label={t('component.pdf.export')}
                design="lightBlue"
                className={styles.button}
                icon="exportToPdf"
                onClick={() => {
                  setShowExportPdf(true);
                  onPdfHandle('portrait');
                }}
                disabled={Boolean(
                  isHeadsetsLoading || isPackagesLoading || isSummaryLoading,
                )}
              />
              <ReactToPrint
                content={() => printRef.current}
                trigger={() => (
                  <Button
                    label={t('general.print')}
                    design="lightBlue"
                    className={styles.button}
                    icon="print"
                    disabled={Boolean(
                      isHeadsetsLoading ||
                        isPackagesLoading ||
                        isSummaryLoading,
                    )}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}
      <StickyTable
        config={account ? headsetBillingConfig : null}
        data={headsetsRecords}
        summaryRecord={summaryHeadsetsRecord}
        isLoading={isHeadsetsLoading}
        className={cl(
          styles.table,
          account?.role === RoleEnum.airpano && styles.airpano,
          tableNumber !== 1 && styles.hideOnLaptop,
        )}
        withPaginator
        stickyCols={stickyCols}
        onSort={(sortOption) => {
          setHeadsetsSortOption(sortOption);
        }}
        autoWidth={account?.role === RoleEnum.airpano}
        isFiltered={isFiltered}
        resetFilter={resetDRPToInit}
        notFoundText={t('general.resetFilters')}
      />
      {account?.role && account.role !== RoleEnum.airpano && (
        <>
          <StickyTable
            config={packageBillingConfig}
            data={packagesRecords}
            isLoading={isPackagesLoading}
            className={cl(
              styles.table,
              tableNumber !== 2 && styles.hideOnLaptop,
            )}
            withPaginator
            onSort={(sortOption) => {
              setPackagesSortOption(sortOption);
            }}
            isFiltered={isFiltered}
            resetFilter={resetDRPToInit}
            notFoundText={t('general.resetFilters')}
          />
          <StickyTable
            config={companiesSummaryConfig}
            data={companiesSummaryData}
            isLoading={isSummaryLoading}
            className={cl(
              styles.table,
              tableNumber !== 3 && styles.hideOnLaptop,
            )}
          />
        </>
      )}
      <PdfHeightChecker
        isActive={pdfHeaderHeightProps.isActive}
        orientation={pdfHeaderHeightProps.orientation}
        clbHeight={pdfHeaderHeightProps.clbHeight}
        onRender={(height) => {
          setPdfHeaderHeightProps((props) => ({
            ...props,
            isActive: false,
            height,
          }));
        }}
      >
        {printTop}
      </PdfHeightChecker>
      <PdfTablePreparer
        isActive={pdfHeadsetsTableProps.isActive}
        orientation={pdfHeadsetsTableProps.orientation}
        config={headsetBillingConfig}
        data={headsetsRecords}
        summaryRecord={summaryHeadsetsRecord}
        sortOption={headsetsSortOption}
        onRender={(params) => {
          setPdfHeadsetsTableProps((props) => ({
            ...props,
            isActive: false,
            params,
          }));
        }}
      />
      <PdfTablePreparer
        isActive={pdfPackagesTableProps.isActive}
        orientation={pdfPackagesTableProps.orientation}
        config={packageBillingConfig}
        data={packagesRecords}
        sortOption={packagesSortOption}
        onRender={(params) => {
          setPdfPackagesTableProps((props) => ({
            ...props,
            isActive: false,
            params,
          }));
        }}
      />
      <PdfTablePreparer
        isActive={pdfCompaniesTableProps.isActive}
        orientation={pdfCompaniesTableProps.orientation}
        config={companiesSummaryConfig}
        data={companiesSummaryData}
        onRender={(params) => {
          setPdfCompaniesTableProps((props) => ({
            ...props,
            isActive: false,
            params,
          }));
        }}
      />
    </div>
  );
};

export default HotelBilling;
